import { NavLink, useLocation, Link } from "react-router-dom";
import { Home, Calendar, Wallet, Building2, MoreHorizontal, Clock } from "lucide-react";
import { useBilling } from "../context/BillingContext";
import { AccountMenu } from "./AccountMenu";
import { isNativePlatform } from "../lib/platform";

export function TrialBanner() {
  const { billing } = useBilling();
  if (!billing?.in_trial || billing.trial_days_left > 5) return null;
  const d = billing.trial_days_left;
  // Native iOS: factual trial status only — no Subscribe CTA and no link to the Stripe paywall.
  if (isNativePlatform()) {
    return (
      <div data-testid="trial-banner" className="block bg-[#0D786A] text-white text-sm px-5 py-2.5">
        <span className="flex items-center justify-center gap-2">
          <Clock size={14} />
          <span>{d} day{d === 1 ? "" : "s"} left in your free trial</span>
        </span>
      </div>
    );
  }
  return (
    <Link to="/paywall" data-testid="trial-banner"
      className="block bg-[#0D786A] text-white text-sm px-5 py-2.5 df-pressable">
      <span className="flex items-center justify-center gap-2">
        <Clock size={14} />
        <span>{d} day{d === 1 ? "" : "s"} left in your free trial</span>
        <span className="font-semibold underline underline-offset-2">Subscribe</span>
      </span>
    </Link>
  );
}

const TABS = [
  { to: "/", label: "Home", icon: Home, id: "home", exact: true },
  { to: "/work", label: "Work", icon: Calendar, id: "work" },
  { to: "/money", label: "Money", icon: Wallet, id: "money" },
  { to: "/practices", label: "Practices", icon: Building2, id: "practices" },
  { to: "/more", label: "More", icon: MoreHorizontal, id: "more" },
];

function isTabActive(t, pathname) {
  return t.exact ? pathname === "/" : pathname.startsWith(t.to);
}

export function TopBar() {
  const { pathname } = useLocation();
  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200/70" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <div className="flex items-center justify-between gap-3 px-5 lg:px-8 h-14">
        <Link to="/" data-testid="brand-home" className="flex items-center gap-2 df-pressable min-w-0">
          <img src="/molaroo-mark.png" alt="Molaroo" className="h-9 w-9 rounded-xl object-contain bg-white shadow-sm shrink-0" />
          <span className="flex flex-col leading-tight min-w-0">
            <span className="font-heading font-bold text-slate-900 text-lg leading-none">Molaroo</span>
            <span className="text-[10px] text-slate-400 tracking-wide leading-none mt-0.5">Powered by NavScora™</span>
          </span>
        </Link>
        <nav className="hidden lg:flex items-center gap-1">
          {TABS.map((t) => (
            <NavLink key={t.id} to={t.to} data-testid={`topnav-${t.id}`}
              className={`px-3 py-2 rounded-xl text-sm font-medium df-pressable transition-colors ${
                isTabActive(t, pathname) ? "bg-[#E6F4F1] text-[#0D786A]" : "text-slate-500 hover:text-slate-800"
              }`}>
              {t.label}
            </NavLink>
          ))}
        </nav>
        <AccountMenu />
      </div>
    </header>
  );
}

export function BottomNav() {
  const { pathname } = useLocation();
  return (
    <nav
      className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md sm:max-w-xl z-50 lg:hidden
                 bg-white/90 backdrop-blur-xl border-t border-slate-200/80 rounded-t-3xl
                 shadow-[0_-4px_24px_-8px_rgba(15,23,42,0.15)] px-2 py-2 flex justify-around items-stretch"
      style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
    >
      {TABS.map((t) => {
        const active = isTabActive(t, pathname);
        const Icon = t.icon;
        return (
          <NavLink
            key={t.id}
            to={t.to}
            data-testid={`nav-tab-${t.id}`}
            className="flex-1 flex flex-col items-center gap-1 py-1.5 rounded-2xl df-pressable"
          >
            <span
              className={`flex items-center justify-center h-8 w-14 rounded-full transition-colors duration-200 ${
                active ? "bg-[#E6F4F1] text-[#0D786A]" : "text-slate-400"
              }`}
            >
              <Icon size={20} strokeWidth={active ? 2.4 : 2} />
            </span>
            <span className={`text-[11px] font-medium ${active ? "text-[#0D786A]" : "text-slate-400"}`}>
              {t.label}
            </span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export function AppShell({ children, hideNav }) {
  return (
    <div className="df-shell df-scroll">
      {!hideNav && <TopBar />}
      <TrialBanner />
      <div className={hideNav ? "" : "pb-[calc(6.5rem+env(safe-area-inset-bottom))] lg:pb-10"}>{children}</div>
      {!hideNav && <BottomNav />}
    </div>
  );
}

export function PageHeader({ title, subtitle, right, onBack }) {
  return (
    <div className="px-5 lg:px-8 pt-6 pb-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        {onBack && (
          <button
            onClick={onBack}
            data-testid="back-button"
            className="text-sm text-[#0D786A] font-medium mb-1 df-pressable"
          >
            ← Back
          </button>
        )}
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 truncate">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {right || null}
    </div>
  );
}
